import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  BarChart as BarChartIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const fmt = (n) => `${Math.round(Number(n || 0)).toLocaleString('hu-HU')} Ft`;
const fmtShort = (n) => {
  const v = Math.round(Number(n || 0));
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}e`;
  return String(v);
};

const PIE_COLORS = [
  '#4caf50', '#2196f3', '#ff9800', '#9c27b0',
  '#f44336', '#00bcd4', '#795548', '#607d8b',
];

function CustomBarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, boxShadow: 3 }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>{label}</Typography>
      {payload.map((p) => (
        <Typography key={p.name} variant="caption" display="block" sx={{ color: p.fill }}>
          {p.name}: {fmt(p.value)}
        </Typography>
      ))}
    </Box>
  );
}

function CustomPieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value, percent } = payload[0];
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, boxShadow: 3 }}>
      <Typography variant="caption" fontWeight={700} display="block">{name}</Typography>
      <Typography variant="caption" display="block">{fmt(value)}</Typography>
      <Typography variant="caption" color="text.secondary">{(percent * 100).toFixed(1)}%</Typography>
    </Box>
  );
}

export default function BudgetAnalytics({ budgetItems, vendorCategories, budgetSummary, isMobile, theme }) {
  const [expanded, setExpanded] = useState(true);

  // Vendor category ID lookup map for resolving names
  const vcMap = useMemo(() => {
    const m = new Map();
    vendorCategories.forEach((vc) => m.set(vc.id, vc));
    return m;
  }, [vendorCategories]);

  const categoryBreakdown = useMemo(() => {
    const map = new Map();
    map.set(null, { id: null, name: 'Kategória nélkül', plannedBudget: null, tervezett: 0, valos: 0, fizetve: 0, osszes: 0 });

    vendorCategories.forEach((vc) => {
      map.set(vc.id, {
        id: vc.id,
        name: vc.name,
        plannedBudget: vc.plannedBudget ? Number(vc.plannedBudget) : null,
        tervezett: 0,
        valos: 0,
        fizetve: 0,
        osszes: 0,
      });
    });

    budgetItems.forEach((item) => {
      // Direct assignment on budget item takes priority;
      // fall back to the linked custom vendor's category
      const key = item.vendorCategoryId ?? item.customVendor?.vendorCategoryId ?? null;
      const bucket = map.get(key) ?? map.get(null);
      bucket.tervezett += Number(item.budgetedAmount || 0);
      bucket.valos     += Number(item.actualAmount   || 0);
      bucket.fizetve   += Number(item.paidAmount     || 0);
      // Effective total: real price where set, otherwise the planned one
      const hasActual = item.actualAmount !== null && item.actualAmount !== undefined && item.actualAmount !== '';
      bucket.osszes    += hasActual ? Number(item.actualAmount) : Number(item.budgetedAmount || 0);
    });

    // Keep ALL vendor categories so they always appear in the progress cards (even if empty).
    // The "Kategória nélkül" bucket is only shown if it actually has items.
    return [...map.values()].filter((b) => b.id !== null || b.tervezett > 0 || b.valos > 0 || b.fizetve > 0);
  }, [budgetItems, vendorCategories]);

  // Bar chart: only show categories with actual amounts (empty ones are in progress cards)
  const barData = useMemo(() =>
    categoryBreakdown
      .filter((b) => b.tervezett > 0 || b.valos > 0 || b.fizetve > 0)
      .map((b) => ({
        name: b.name.length > 16 ? b.name.slice(0, 15) + '…' : b.name,
        fullName: b.name,
        Tervezett: b.tervezett,
        Valós: b.valos,
        Fizetve: b.fizetve,
      })),
  [categoryBreakdown]);

  const pieData = useMemo(() =>
    categoryBreakdown
      .filter((b) => b.tervezett > 0)
      .map((b, i) => ({ name: b.name, value: b.tervezett, color: PIE_COLORS[i % PIE_COLORS.length] })),
  [categoryBreakdown]);

  const vendorRows = useMemo(() =>
    [...budgetItems]
      .sort((a, b) => Number(b.budgetedAmount || 0) - Number(a.budgetedAmount || 0))
      .map((item) => {
        const vcId = item.vendorCategoryId ?? item.customVendor?.vendorCategoryId ?? null;
        const vcName = item.vendorCategory?.name ?? (vcId ? vcMap.get(vcId)?.name : null);
        const hasActual = item.actualAmount !== null && item.actualAmount !== undefined && item.actualAmount !== '';
        const effective = hasActual ? Number(item.actualAmount) : Number(item.budgetedAmount || 0);
        return {
          id: item.id,
          name: item.booking?.provider?.businessName || item.customVendor?.name || item.description,
          isAuto: item.isAutoSynced,
          tervezett: Number(item.budgetedAmount || 0),
          valos: Number(item.actualAmount   || 0),
          fizetve: Number(item.paidAmount   || 0),
          hatraler: effective - Number(item.paidAmount || 0),
          vendorCategory: vcName,
        };
      }),
  [budgetItems, vcMap]);

  const chartHeight = isMobile ? 300 : 380;
  const isVertical  = isMobile || barData.length > 5;
  const totalBudgeted = Number(budgetSummary?.totalBudgeted || 0);

  const showPie = pieData.length >= 2;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, v) => setExpanded(v)}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px !important',
        '&:before': { display: 'none' },
        mb: 3,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <BarChartIcon fontSize="small" color="primary" />
          <Typography fontWeight={600}>Elemzések és diagramok</Typography>
          <Chip label={`${categoryBreakdown.length} kategória`} size="small" variant="outlined" />
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 2.5 }}>

        {/* A+B: Bar chart + Donut */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* A: Grouped bar chart */}
          <Grid item xs={12} md={showPie ? 7 : 12}>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Kategória összehasonlítás</Typography>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart
                data={barData}
                layout={isVertical ? 'vertical' : 'horizontal'}
                margin={{ top: 4, right: 8, left: isVertical ? 0 : 4, bottom: 4 }}
              >
                {isVertical ? (
                  <>
                    <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={58} />
                  </>
                )}
                <Tooltip content={<CustomBarTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="Tervezett" fill={theme.palette.primary.light}  radius={isVertical ? [0, 3, 3, 0] : [3, 3, 0, 0]} maxBarSize={24} />
                <Bar dataKey="Valós"     fill={theme.palette.secondary.main} radius={isVertical ? [0, 3, 3, 0] : [3, 3, 0, 0]} maxBarSize={24} />
                <Bar dataKey="Fizetve"   fill={theme.palette.success.main}   radius={isVertical ? [0, 3, 3, 0] : [3, 3, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </Grid>

          {/* B: Donut – min 2 categories */}
          {showPie && (
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Megoszlás kategóriánként</Typography>
              {/* A Legend-et szándékosan NEM a PieChart-on belül rendereljük: az foglalna
                  helyet ugyanabban a koordináta-dobozban, amihez a Pie cx/cy %-a
                  igazodik, és ezért a "közép" valójában nem középen lenne. Így a Pie
                  cx/cy="50%" a konténer tényleges közepe, az overlay felirat pedig
                  sima CSS top/left 50%-kal pontosan odaigazítható. */}
              <Box sx={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius="50%"
                      outerRadius="72%"
                      paddingAngle={2}
                      dataKey="value"
                      labelLine={false}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                  maxWidth: `${chartHeight * 0.36}px`,
                }}>
                  <Typography variant="caption" color="text.secondary" display="block"
                    sx={{ lineHeight: 1.2, fontSize: isMobile ? '0.6rem' : '0.7rem' }}>
                    Összes
                  </Typography>
                  <Typography fontWeight={700} sx={{ lineHeight: 1.2, fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                    {fmtShort(totalBudgeted)}
                  </Typography>
                </Box>
              </Box>
              {/* Saját, egyszerű jelmagyarázat a diagram alatt (nem recharts <Legend>,
                  hogy ne foglaljon helyet a Pie koordináta-dobozában). */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 1 }}>
                {pieData.map((entry) => (
                  <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: entry.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12 }}>{entry.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>

        {/* C: Kifizetési előrehaladás kategóriánként */}
        <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Kifizetési előrehaladás kategóriánként</Typography>

        {isMobile ? (
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden', mb: 3 }}>
            {categoryBreakdown.map((b, i) => {
              const keret    = b.plannedBudget ?? b.tervezett;
              const isEmpty  = b.tervezett === 0 && b.valos === 0 && b.fizetve === 0;
              // Valós ár, ahol már ismert, különben a tervezett — a ki nem töltött tétel nem 0 Ft
              const over     = !isEmpty && keret > 0 && b.osszes > keret;
              const pctFiz   = b.osszes > 0 ? Math.min((b.fizetve / b.osszes) * 100, 100) : 0;
              const egyenleg = b.osszes - keret;

              return (
                <React.Fragment key={b.id ?? 'none'}>
                  {i > 0 && <Divider />}
                  <Box sx={{
                    py: 1,
                    px: 1.5,
                    opacity: isEmpty ? 0.6 : 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                    {/* Sor 1: név + egyenleg */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: isEmpty ? 0 : 0.5, gap: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={b.name} sx={{ flex: 1 }}>{b.name}</Typography>
                      {isEmpty ? (
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {b.plannedBudget != null ? `Keret: ${fmt(b.plannedBudget)}` : 'Nincs tétel'}
                        </Typography>
                      ) : (
                        <Typography variant="caption" fontWeight={700} color={over ? 'error.main' : 'success.main'} sx={{ flexShrink: 0 }}>
                          {egyenleg > 0
                            ? `+${fmt(egyenleg)} túllépés`
                            : egyenleg < 0
                            ? `${fmt(Math.abs(egyenleg))} szabad`
                            : 'Egyenlő'}
                        </Typography>
                      )}
                    </Box>
                    {/* Sor 2: progress + fizetve% */}
                    {!isEmpty && (
                      <>
                        <LinearProgress variant="determinate" value={pctFiz} sx={{
                          height: 5, borderRadius: 3, bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: over ? 'error.main' : 'success.main', borderRadius: 3 },
                        }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">
                            {b.plannedBudget != null ? 'Keret' : 'Tervezett'}: {fmt(keret)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Fizetve: {fmt(b.fizetve)} ({pctFiz.toFixed(0)}%)
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        ) : (
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {categoryBreakdown.map((b) => {
              const keret    = b.plannedBudget ?? b.tervezett;
              const isEmpty  = b.tervezett === 0 && b.valos === 0 && b.fizetve === 0;
              // Valós ár, ahol már ismert, különben a tervezett — a ki nem töltött tétel nem 0 Ft
              const over     = !isEmpty && keret > 0 && b.osszes > keret;
              const pctValos = keret > 0 ? Math.min((b.valos   / keret) * 100, 100) : 0;
              const pctFiz   = b.osszes > 0 ? Math.min((b.fizetve / b.osszes) * 100, 100) : 0;
              const egyenleg = b.osszes - keret;

              return (
                <Grid item xs={12} sm={6} md={4} key={b.id ?? 'none'}>
                  <Card elevation={0} sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    height: '100%',
                    opacity: isEmpty ? 0.6 : 1,
                  }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body2" fontWeight={700} noWrap title={b.name} mb={0.75}>{b.name}</Typography>

                      {isEmpty ? (
                        <Typography variant="caption" color="text.secondary">
                          {b.plannedBudget != null
                            ? `Keret: ${fmt(b.plannedBudget)} – még nincs tétel`
                            : 'Még nincs hozzárendelt tétel'}
                        </Typography>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25, gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                              {b.plannedBudget != null ? 'Keret' : 'Tervezett'}: <strong>{fmt(keret)}</strong>
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color={over ? 'error.main' : 'success.main'} noWrap>
                              {egyenleg > 0
                                ? `+${fmt(egyenleg)} túllépés`
                                : egyenleg < 0
                                ? `${fmt(Math.abs(egyenleg))} szabad`
                                : 'Egyenlő'}
                            </Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={pctValos} sx={{
                            height: 6, borderRadius: 3, mb: 0.75, bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': { bgcolor: over ? 'error.main' : 'secondary.main', borderRadius: 3 },
                          }} />

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">
                              Fizetve: <strong>{fmt(b.fizetve)}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{pctFiz.toFixed(0)}%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={pctFiz} sx={{
                            height: 5, borderRadius: 3, bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 3 },
                          }} />
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* D: Tételek részletezése — mobilon kimarad: alatta a tételkártyák ugyanezt mutatják (szerkesztéssel) */}
        {!isMobile && (
          <>
          <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Tételek részletezése</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 12, color: 'text.secondary', borderBottom: `2px solid ${theme.palette.divider}`, whiteSpace: 'nowrap' } }}>
                  <TableCell>Szolgáltató / Tétel</TableCell>
                  <TableCell align="right">Tervezett</TableCell>
                  <TableCell align="right">Valós</TableCell>
                  <TableCell align="right">Fizetve</TableCell>
                  <TableCell align="right">Hátralék</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorRows.map((row) => (
                  <TableRow key={row.id} sx={{ '&:last-child td': { border: 0 }, verticalAlign: 'top' }}>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>{row.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {row.isAuto
                          ? <Chip label="Auto" size="small" icon={<SyncIcon style={{ fontSize: 12 }} />} variant="outlined" color="primary" />
                          : <Chip label="Kézi" size="small" variant="outlined" />}
                        {row.vendorCategory && (
                          <Chip label={row.vendorCategory} size="small" variant="outlined" color="secondary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{fmt(row.tervezett)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color={row.valos > row.tervezett ? 'error.main' : 'text.primary'}>
                        {row.valos > 0 ? fmt(row.valos) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{fmt(row.fizetve)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" fontWeight={600} color={row.hatraler > 0 ? 'error.main' : 'success.main'}>
                        {fmt(row.hatraler)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          </>
        )}

      </AccordionDetails>
    </Accordion>
  );
}
